import { GlobalOutlined, PlusOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Empty, Form, Input, Modal, Skeleton, Tag, Typography, message } from 'antd'
import { useNavigate } from 'react-router-dom'
import { createWorkspaceSite } from '../../api'
import type { SaasApiError } from '../../api'
import { useWorkspaceState } from '../WorkspaceContext'
import { useState } from 'react'

const { Title, Text } = Typography

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * "Website" nav entry landing page — the LiangCollection -> Vistoera
 * Website Builder vertical slice's first real Product Surface (spec §4/
 * §6): lists this Workspace's Sites and lets the user actually create
 * one through a real API call, never a mock array. Reuses the exact
 * `createWorkspaceSite`/`listWorkspaceSites` contract `WorkspaceContext`
 * already wires up for the Overview/Domains/Publish pages — no second
 * Site-creation code path.
 */
export default function WebsiteHomePage() {
  const { workspace, sites, loading, error, reload } = useWorkspaceState()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form] = Form.useForm()

  const onNameChange = (value: string) => {
    const currentSlug = form.getFieldValue('slug')
    const previousAutoSlug = slugify(form.getFieldValue('_lastAutoSlug') || '')
    if (!currentSlug || currentSlug === previousAutoSlug) {
      const nextSlug = slugify(value)
      form.setFieldsValue({ slug: nextSlug, _lastAutoSlug: value })
    }
  }

  const onCreate = async () => {
    const values = await form.validateFields()
    setCreating(true)
    try {
      const { data: site } = await createWorkspaceSite(workspace!.id, {
        name: values.name.trim(),
        slug: values.slug.trim(),
      })
      message.success(`网站「${site.name}」已创建`)
      setCreateOpen(false)
      form.resetFields()
      reload()
      navigate(`/workspace/${workspace!.id}/website/${site.id}`)
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setCreating(false)
    }
  }

  if (error) {
    return <Alert type="error" message={error.message} showIcon style={{ margin: 24 }} />
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            网站
          </Title>
          <Text type="secondary">创建网站，添加条目与分类，并预览生成的网站效果。</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新建网站
        </Button>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : sites.length === 0 ? (
        <Empty description="暂无网站，创建一个开始建站" style={{ marginTop: 80 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            新建网站
          </Button>
        </Empty>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {sites.map((site) => (
            <Card
              key={site.id}
              hoverable
              title={
                <span>
                  <GlobalOutlined style={{ marginRight: 8 }} />
                  {site.name}
                </span>
              }
              onClick={() => navigate(`/workspace/${workspace!.id}/website/${site.id}`)}
            >
              <Text type="secondary">/{site.slug}</Text>
              <div style={{ marginTop: 12 }}>
                <Tag color={site.status === 'active' ? 'green' : 'default'}>{site.status}</Tag>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title="新建网站"
        open={createOpen}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        okText="创建"
        cancelText="取消"
        confirmLoading={creating}
      >
        <Form form={form} layout="vertical" initialValues={{ name: '', slug: '' }}>
          <Form.Item
            name="name"
            label="网站名称"
            rules={[{ required: true, message: '请输入网站名称' }]}
          >
            <Input placeholder="梁氏收藏" onChange={(e) => onNameChange(e.target.value)} />
          </Form.Item>
          <Form.Item
            name="slug"
            label="标识（Slug）"
            rules={[{ required: true, message: '请输入标识' }, { pattern: /^[a-z0-9-]+$/, message: '仅支持小写字母、数字和短横线' }]}
            extra="用于网站的内部标识，根据名称自动生成。"
          >
            <Input placeholder="liangcollection" />
          </Form.Item>
          <Form.Item name="_lastAutoSlug" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
